import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { photographerService } from '../../../services/photographerService';
import { locationService, calculateDistance, getDistanceLabel } from '../../../services/locationService';
import { useUserLocation } from '../../../contexts/UserLocationContext';
import {
  buildNearbyPhotographerCards,
  mergeCoversIntoNearbyCards,
} from '../../../utils/nearbyPhotographerCards';
import { getPhotographerUserId } from '../../../services/portfolioCoverService';
import { Capacitor } from '@capacitor/core';
import { getCurrentPositionFast, isGeolocationSupported } from '../../../utils/location';
import Icon from '../../../components/AppIcon';
import DroneIcon from '../../../components/icons/DroneIcon';
import Image from '../../../components/AppImage';
import MashhadLogo from '../../../components/MashhadLogo';

const SHOOTING_METHOD_ICONS = {
  professional_camera: { Icon, name: 'Camera' },
  mobile_camera: { Icon, name: 'Smartphone' },
  drone: { Icon: DroneIcon, name: null },
};
import Button from '../../../components/ui/Button';
import NearbyPhotographersMap from '../../../components/NearbyPhotographersMap';
import GlobalLocationModal from '../../../components/location/GlobalLocationModal';

const REFETCH_DISTANCE_THRESHOLD_KM = 1;
const NEARBY_COVER_PREFETCH_COUNT = 4;

async function publishNearbyPhotographersList(
  rawList,
  generation,
  loadGenerationRef,
  { setPhotographers, setLoading, hasPhotographersListRef },
) {
  const isStale = () =>
    generation != null && generation !== loadGenerationRef.current;

  const baseCards = buildNearbyPhotographerCards(rawList);
  if (isStale()) return;
  setPhotographers(baseCards);
  hasPhotographersListRef.current = baseCards.length > 0;
  setLoading(false);

  const coverIds = baseCards
    .slice(0, NEARBY_COVER_PREFETCH_COUNT)
    .map(getPhotographerUserId)
    .filter(Boolean);
  if (!coverIds.length) return;

  const withCovers = await mergeCoversIntoNearbyCards(baseCards, coverIds);
  if (isStale()) return;
  setPhotographers(withCovers);
}

const NearbyPhotographers = () => {
  console.log('🔵 NearbyPhotographers component mounted');
  const navigate = useNavigate();
  const { userLocation: contextLocation, locationStatus: contextStatus } = useUserLocation();
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationPermission, setLocationPermission] = useState(null); // null, 'granted', 'denied', 'requesting'
  const [userLocation, setUserLocation] = useState(null);
  const [error, setError] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [isInsecureContext, setIsInsecureContext] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);

  const lastLoadedCoordsRef = useRef(null);
  const hasPhotographersListRef = useRef(false);
  const fallbackHandledRef = useRef(false);
  const loadGenerationRef = useRef(0);

  // Default city coordinates (Riyadh)
  const defaultCityCoords = { lat: 24.7136, lng: 46.6753 };

  // Check if context is secure (native app = always secure for geolocation)
  const checkSecureContext = () => {
    if (typeof window === 'undefined') return false;
    if (Capacitor.isNativePlatform()) return true;
    const hostname = window.location.hostname;
    const isLocalNetwork = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(hostname);
    return window.isSecureContext ||
           window.location.protocol === 'https:' ||
           hostname === 'localhost' ||
           hostname === '127.0.0.1' ||
           isLocalNetwork;
  };

  // Use default location (Riyadh)
  const useDefaultLocation = async () => {
    setShowLocationPrompt(false);
    setLocationPermission('denied');
    setUserLocation(defaultCityCoords);
    setError(null);
    setIsInsecureContext(false);
    setPermissionDenied(false);
    await loadPhotographersWithLocation(defaultCityCoords);
  };

  const syncFromContext = (coords, { forceInitialLoad = false } = {}) => {
    const lat = coords?.lat;
    const lng = coords?.lng;
    if (lat == null || lng == null) return;

    const location = { lat: Number(lat), lng: Number(lng) };
    const prev = lastLoadedCoordsRef.current;

    if (prev && !forceInitialLoad) {
      const movedKm = calculateDistance(prev.lat, prev.lng, location.lat, location.lng);
      if (movedKm <= REFETCH_DISTANCE_THRESHOLD_KM) return;
    }

    const softRefresh = hasPhotographersListRef.current && prev != null;
    setUserLocation(location);
    setLocationPermission('granted');
    setShowLocationPrompt(false);
    setError(null);
    lastLoadedCoordsRef.current = location;
    loadPhotographersWithLocation(location, { softRefresh });
  };

  // Reactive load from UserLocationContext (fast onboarding + background accurate refine)
  useEffect(() => {
    if (contextStatus === 'granted' && contextLocation?.lat != null && contextLocation?.lng != null) {
      syncFromContext(contextLocation);
      return;
    }
    if (contextStatus === 'loading') {
      setLocationPermission('requesting');
    }
  }, [contextStatus, contextLocation?.lat, contextLocation?.lng]);

  // Fallback when context has not supplied coordinates (once per mount)
  useEffect(() => {
    if (fallbackHandledRef.current) return;

    const settled = ['denied', 'error', 'timeout', 'insecure', 'idle'].includes(contextStatus);
    if (!settled) return;

    if (contextLocation?.lat != null && contextLocation?.lng != null) {
      return;
    }

    fallbackHandledRef.current = true;

    if (!isGeolocationSupported()) {
      setLocationPermission('denied');
      loadPhotographersWithoutLocation();
      return;
    }
    const isSecure = checkSecureContext();
    if (!isSecure) {
      setIsInsecureContext(true);
      setLocationPermission('denied');
      loadPhotographersWithoutLocation();
      return;
    }

    const locationChoice = localStorage.getItem('locationPermissionChoice');
    if (locationChoice === 'granted') {
      // Onboarding already requested permission — wait for context coords, no blocking GPS here
      setLocationPermission('requesting');
      if (!hasPhotographersListRef.current) {
        loadPhotographersWithoutLocation();
      }
      return;
    }
    if (locationChoice === 'denied') {
      setLocationPermission('denied');
      loadPhotographersWithoutLocation();
      return;
    }

    if (Capacitor.isNativePlatform()) {
      setShowLocationPrompt(true);
      loadPhotographersWithoutLocation();
    } else {
      requestLocationAndLoadPhotographers();
    }
  }, [contextStatus, contextLocation?.lat, contextLocation?.lng]);

  const handleAllowLocation = async () => {
    // Check if we're in a secure context before requesting
    const isSecure = checkSecureContext();
    
    if (!isSecure) {
      setIsInsecureContext(true);
      setShowLocationPrompt(false);
      setLocationPermission('denied');
      return;
    }

    setShowLocationPrompt(false);
    setPermissionDenied(false);
    setIsInsecureContext(false);
    localStorage.setItem('locationPermissionChoice', 'granted');
    await requestLocationAndLoadPhotographers();
  };

  const handleRetryPermission = async () => {
    setPermissionDenied(false);
    setError(null);
    await requestLocationAndLoadPhotographers();
  };

  const handleDenyLocation = () => {
    setShowLocationPrompt(false);
    setLocationPermission('denied');
    localStorage.setItem('locationPermissionChoice', 'denied');
    // Photographers are already loaded without location
  };

  const requestLocationAndLoadPhotographers = async () => {
    try {
      if (!checkSecureContext()) {
        setIsInsecureContext(true);
        setLocationPermission('denied');
        await loadPhotographersWithoutLocation();
        return;
      }

      setLocationPermission('requesting');
      setLoading(true);
      
      const position = await getCurrentPositionFast();

      const { latitude, longitude } = position.coords;
      setUserLocation({ lat: latitude, lng: longitude });
      setLocationPermission('granted');
      localStorage.setItem('locationPermissionChoice', 'granted');
      setPermissionDenied(false);
      setIsInsecureContext(false);

      const coords = { lat: latitude, lng: longitude };
      lastLoadedCoordsRef.current = coords;
      await loadPhotographersWithLocation(coords);
    } catch (err) {
      console.error('Error getting location or loading photographers:', err);
      
      // Handle different error codes
      if (err.code === 1) {
        // Permission denied by user
        setLocationPermission('denied');
        setPermissionDenied(true);
        localStorage.setItem('locationPermissionChoice', 'denied');
        setError('تم رفض إذن الموقع. يمكنك تفعيله من إعدادات المتصفح أو استخدام موقع افتراضي.');
        await loadPhotographersWithoutLocation();
      } else if (err.code === 2) {
        // Position unavailable
        setLocationPermission('denied');
        setError('تعذر تحديد موقعك. سيتم عرض جميع المصورين المتاحين.');
        await loadPhotographersWithoutLocation();
      } else if (err.code === 3) {
        // Timeout
        setLocationPermission('denied');
        setError('انتهت مهلة تحديد الموقع. سيتم عرض جميع المصورين المتاحين.');
        await loadPhotographersWithoutLocation();
      } else {
        // Other errors (including secure context errors)
        setLocationPermission('denied');
        const errorMessage = err.message?.includes('secure context') 
          ? 'تحديد الموقع يتطلب اتصال آمن (HTTPS). سيتم عرض جميع المصورين المتاحين.'
          : 'فشل الحصول على موقعك. سيتم عرض جميع المصورين المتاحين.';
        setError(errorMessage);
        await loadPhotographersWithoutLocation();
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPhotographersWithLocation = async (location, { softRefresh = false } = {}) => {
    const generation = ++loadGenerationRef.current;
    if (!softRefresh) {
      setLoading(true);
    }
    console.log('📍 loadPhotographersWithLocation called with:', location, { softRefresh });
    try {
      const allPhotographers = await photographerService.getAllPhotographers();
      if (generation !== loadGenerationRef.current) return;
      console.log('📸 All photographers from service (with location):', allPhotographers.length);
      
      // Debug: log first photographer's location data
      if (allPhotographers.length > 0) {
        const p = allPhotographers[0];
        console.log('📸 Sample photographer location data:', {
          serviceCenterLat: p?.serviceCenterLat,
          service_center_lat: p?.service_center_lat,
          serviceCenterLng: p?.serviceCenterLng,
          service_center_lng: p?.service_center_lng,
          baseLat: p?.baseLat,
          base_lat: p?.base_lat
        });
      }
      
      const photographersWithDistance = allPhotographers
        .filter(p => {
          // Use service_center if available, otherwise fallback to base_lat/base_lng for backward compatibility
          const serviceLat = p?.serviceCenterLat || p?.service_center_lat;
          const serviceLng = p?.serviceCenterLng || p?.service_center_lng;
          const baseLat = p?.baseLat || p?.base_lat;
          const baseLng = p?.baseLng || p?.base_lng;
          
          // Prefer service_center, fallback to base location
          const lat = serviceLat || baseLat;
          const lng = serviceLng || baseLng;
          
          const hasValidLocation = lat && lng && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng));
          console.log('📸 Photographer location check:', { 
            serviceLat, serviceLng, baseLat, baseLng, hasValidLocation 
          });
          return hasValidLocation;
        })
        .map(p => {
          // Use service_center if available, otherwise fallback to base location
          const serviceLat = p?.serviceCenterLat || p?.service_center_lat;
          const serviceLng = p?.serviceCenterLng || p?.service_center_lng;
          const baseLat = p?.baseLat || p?.base_lat;
          const baseLng = p?.baseLng || p?.base_lng;
          
          const photographerLat = parseFloat(serviceLat || baseLat);
          const photographerLng = parseFloat(serviceLng || baseLng);
          const serviceRadius = p?.serviceRadiusKm || p?.service_radius_km || 20;
          
          const distanceKm = calculateDistance(location.lat, location.lng, photographerLat, photographerLng);
          const roundedDistance = Math.round(distanceKm * 10) / 10;
          
          console.log('📏 Distance calculation:', {
            userLat: location.lat,
            userLng: location.lng,
            photographerLat,
            photographerLng,
            distanceKm: roundedDistance
          });

          return {
            ...p,
            distanceKm: roundedDistance,
            distanceLabel: getDistanceLabel(distanceKm),
            serviceRadiusKm: serviceRadius,
            // Filter out if distance exceeds service radius (optional filter)
            withinServiceRadius: distanceKm <= serviceRadius
          };
        })
        .filter(p => p.withinServiceRadius !== false) // Optional: filter by service radius
        .sort((a, b) => a.distanceKm - b.distanceKm)
        .slice(0, 20);

      await publishNearbyPhotographersList(
        photographersWithDistance,
        generation,
        loadGenerationRef,
        { setPhotographers, setLoading, hasPhotographersListRef },
      );
    } catch (err) {
      if (generation !== loadGenerationRef.current) return;
      console.error('Error loading photographers with location:', err);
      setError('فشل تحميل المصورين القريبين.');
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  const loadPhotographersWithoutLocation = async () => {
    try {
      setLoading(true);
      const allPhotographers = await photographerService.getAllPhotographers();

      const availablePhotographers = allPhotographers
        .filter(p => {
          const serviceLat = p?.serviceCenterLat || p?.service_center_lat;
          const serviceLng = p?.serviceCenterLng || p?.service_center_lng;
          const baseLat = p?.baseLat || p?.base_lat;
          const baseLng = p?.baseLng || p?.base_lng;
          const lat = serviceLat || baseLat;
          const lng = serviceLng || baseLng;
          const hasLocation = lat != null && lng != null && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng));
          const isAvailable = (p.availableForBooking || p.available_for_booking) !== false;
          return hasLocation && isAvailable;
        })
        .map(p => ({
          ...p,
          distanceKm: p.distanceKm ?? null,
          distanceLabel: p.distanceLabel || null,
        }))
        .slice(0, 20);

      await publishNearbyPhotographersList(
        availablePhotographers,
        null,
        loadGenerationRef,
        { setPhotographers, setLoading, hasPhotographersListRef },
      );
    } catch (err) {
      console.error('Error loading photographers:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  // Map experience level to Arabic label
  const getExperienceLabel = (level) => {
    const levelMap = {
      'certified': 'معتمد',
      'distinguished': 'مميز',
      'elite': 'نخبة',
      // Legacy
      'beginner': 'معتمد',
      'intermediate': 'مميز',
      'advanced': 'مميز',
      'expert': 'نخبة'
    };
    return levelMap[level] || 'معتمد';
  };

  if (loading) {
    return (
      <div className="py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-slate-600">جاري تحميل المصورين القريبين...</p>
          </div>
        </div>
      </div>
    );
  }

  // Always show section
  const displayPhotographers = photographers.slice(0, 4);
  const hasMore = photographers.length > 4;

  // Debug log
  console.log('NearbyPhotographers render:', { loading, photographersCount: photographers.length, userLocation });

  return (
    <div className="py-16 md:py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        {/* Location Permission Prompt */}
        {showLocationPrompt && (
          <div className="mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="bg-gradient-to-r from-primary/10 via-blue-50 to-primary/5 border-2 border-primary/20 rounded-2xl p-6 md:p-8 shadow-lg">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="MapPin" size={28} className="text-primary" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">
                    السماح بتحديد موقعك
                  </h3>
                  <p className="text-slate-600 mb-2 leading-relaxed">
                    لتجربة أفضل، نود تحديد موقعك الدقيق لعرض المصورين القريبين منك. هذا سيساعدك في العثور على أفضل المصورين في منطقتك.
                    {Capacitor.isNativePlatform() && (
                      <span className="block mt-2 font-medium text-slate-700">
                        اضغط أدناه لتفعيل الموقع وعرض المصورين القريبين منك.
                      </span>
                    )}
                  </p>
                  {(!window.isSecureContext && window.location.protocol !== 'https:' && 
                    window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                      <p className="text-xs text-amber-800 flex items-center gap-2">
                        <Icon name="AlertCircle" size={16} />
                        <span>ملاحظة: تحديد الموقع يتطلب اتصال آمن (HTTPS) أو localhost</span>
                      </p>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={handleAllowLocation}
                      className="flex-1 sm:flex-initial"
                      iconName="MapPin"
                      iconPosition="right"
                    >
                      السماح بتحديد الموقع
                    </Button>
                    <Button
                      onClick={handleDenyLocation}
                      variant="outline"
                      className="flex-1 sm:flex-initial"
                    >
                      تخطي
                    </Button>
                  </div>
                </div>
                <button
                  onClick={handleDenyLocation}
                  className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label="إغلاق"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Section Header */}
        <div className="flex items-center justify-between mb-8 md:mb-12">
          <div>
            <div className="inline-block mb-3 px-4 py-1.5 bg-primary/10 rounded-full">
              <span className="text-primary text-sm font-semibold flex items-center gap-2">
                <Icon name="MapPin" size={16} />
                {userLocation ? 'مصورين بالقرب منك' : 'مصورين متاحين'}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              {userLocation ? 'مصورين بالقرب منك' : 'مصورين متاحين'}
            </h2>
            <p className="text-slate-600">
              {userLocation 
                ? `اكتشف أفضل المصورين في منطقتك (${photographers.length} مصور متاح)`
                : photographers.length > 0 
                  ? `اكتشف أفضل المصورين المحترفين (${photographers.length} مصور متاح)`
                  : 'اكتشف أفضل المصورين المحترفين'
              }
            </p>
          </div>
          
          {photographers.length > 0 && (
            <Button
              variant="outline"
              onClick={() => navigate('/browse-photographers')}
              className="hidden md:flex items-center gap-2"
            >
              <span>عرض الكل</span>
              <Icon name="ArrowLeft" size={18} />
            </Button>
          )}
        </div>

        {/* no warning messages — location denial is a user choice, respect it silently */}

        {/* Google Maps Section - Hidden per requirements */}
        {false && userLocation && photographers.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Icon name="Map" size={20} className="text-primary" />
              <span>خريطة المصورين القريبين</span>
            </h3>
            <NearbyPhotographersMap
              userLocation={userLocation}
              photographers={photographers}
              loading={loading}
            />
          </div>
        )}

        {/* No Photographers Message */}
        {!loading && photographers.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Camera" size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 text-lg">لا يوجد مصورين متاحين في منطقتك حالياً</p>
            <Button
              variant="outline"
              onClick={() => navigate('/browse-photographers')}
              className="mt-4"
              iconName="Search"
              iconPosition="right"
            >
              تصفح جميع المصورين
            </Button>
          </div>
        )}

        {/* Scrollable Cards Container */}
        {photographers.length > 0 && (
        <div className="relative">
          <div className="overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4 md:mx-0 md:px-0">
            <div className="flex gap-4 md:gap-6 min-w-max md:min-w-0 md:grid md:grid-cols-2 lg:grid-cols-4">
              {displayPhotographers.map((photographer, index) => {
                const photographerId = photographer.id || photographer.userId || photographer.user_id;
                
                // Debug: Log photographer ID and full object
                console.log('📸 Photographer card data:', {
                  id: photographer.id,
                  userId: photographer.userId,
                  user_id: photographer.user_id,
                  selectedId: photographerId,
                  name: photographer.name,
                  fullObject: photographer
                });
                
                if (!photographerId) {
                  console.warn('⚠️ Missing photographer ID:', photographer);
                }
                
                const handleCardClick = (e) => {
                  // Don't navigate if clicking on button
                  if (e.target.closest('button')) {
                    return;
                  }
                  if (photographerId) {
                    navigate(`/photographers/${photographerId}`);
                  } else {
                    console.error('Cannot navigate: photographerId is missing', photographer);
                  }
                };
                
                return (
                <div
                  key={photographerId || `photographer-${Math.random()}`}
                  onClick={handleCardClick}
                  className="group w-80 md:w-auto flex-shrink-0 bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover:border-primary/20 cursor-pointer"
                >
                  {/* Portfolio Image Cover */}
                  <div className="relative h-64 md:h-72 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                    {photographer.portfolioImage ? (
                      <Image
                        src={photographer.portfolioImage}
                        fallbackSrc={photographer.portfolioImageOriginal}
                        priority={index < 4}
                        alt={photographer.portfolioImageAlt || 'صورة من معرض أعمال المصور'}
                        className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                        <MashhadLogo size="xl" variant="dark" />
                      </div>
                    )}
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>
                    
                    {/* Distance Badge - Always show */}
                    <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-md text-slate-900 px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-semibold shadow-lg border border-white/50 z-10">
                      <Icon name="Navigation" size={12} className="text-primary" />
                      <span className="text-primary font-bold">
                        {photographer.distanceKm !== null && photographer.distanceKm !== undefined
                          ? (photographer.distanceLabel || `${photographer.distanceKm.toFixed(1)} كم`)
                          : '-- كم'}
                      </span>
                    </div>

                    {/* Photographer Info Overlay */}
                    <div className="absolute bottom-4 right-4 left-4 z-10 flex items-center gap-3">
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#1e40af]/30 via-[#0891b2]/30 to-[#475569]/30 backdrop-blur-sm rounded-full blur-md"></div>
                        <Image
                          src={photographer.avatar || photographer.image || photographer.user?.avatarUrl || photographer.user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(photographer.name || 'مصور')}&background=1e40af&color=fff&size=128`}
                          fallbackSrc={photographer.avatarOriginal}
                          priority={index < 4}
                          alt={photographer.avatarAlt || photographer.imageAlt || `صورة ${photographer.name}`}
                          className="relative w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-4 border-white shadow-2xl ring-2 ring-white/50"
                        />
                      </div>
                      
                      {/* Name & City - Together on the same side */}
                      <div className="text-white flex-1 min-w-0" dir="rtl">
                        <h3 className="text-base md:text-lg font-bold drop-shadow-lg line-clamp-1 mb-1 text-right">
                          {photographer.name}
                        </h3>
                        <div className="flex items-center gap-1.5 text-xs text-white/90 drop-shadow-md text-right">
                          <Icon name="MapPin" size={12} className="flex-shrink-0" />
                          <span className="line-clamp-1">{photographer.city || 'الرياض'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4 md:p-5 bg-white">
                    {/* Rating & Reviews */}
                    <div className="flex items-center justify-between gap-4 mb-3 pb-3 border-b border-slate-100">
                      <div className="flex items-center gap-1.5">
                        <Icon name="Star" size={16} className="text-amber-400 fill-amber-400" />
                        <span className="font-bold text-slate-900 text-sm">{formatNumber(photographer.rating || 0)}</span>
                        <span className="text-slate-500 text-xs">({formatNumber(photographer.reviewCount || photographer.totalReviews || 0)})</span>
                      </div>
                      {photographer.isVerified && (
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                          <Icon name="BadgeCheck" size={12} />
                          <span>موثق</span>
                        </div>
                      )}
                    </div>

                    {/* الخبرة + وسائل التصوير في صف واحد */}
                    <div className="mb-3 pb-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-slate-500">مستوى الخبرة:</span>
                        {(() => {
                          const level = getExperienceLabel(photographer.experienceLevel || photographer.experience_level);
                          const isElite = level.includes('نخبة');
                          const isDist  = level.includes('مميز');
                          const iconName = isElite ? 'Crown' : isDist ? 'Star' : 'BadgeCheck';
                          const badgeCls = isElite
                            ? 'bg-violet-100 text-violet-700 border-violet-300'
                            : isDist
                            ? 'bg-amber-100 text-amber-700 border-amber-300'
                            : 'bg-emerald-100 text-emerald-700 border-emerald-300';
                          return (
                            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${badgeCls}`}>
                              <Icon name={iconName} size={12} />
                              <span>{level}</span>
                            </div>
                          );
                        })()}
                      </div>
                      {(photographer.shootingMethods || photographer.shooting_methods)?.length > 0 && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 shadow-sm">
                          {(Array.isArray(photographer.shootingMethods) ? photographer.shootingMethods : photographer.shooting_methods || []).map((method) => {
                            const config = SHOOTING_METHOD_ICONS[method];
                            if (!config) return null;
                            const IconComponent = config.Icon;
                            return (
                              <span key={method} className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/80 text-primary border border-primary/10">
                                {config.name ? (
                                  <IconComponent name={config.name} size={16} />
                                ) : (
                                  <IconComponent size={16} className="text-primary" />
                                )}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Specialties + عداد الباقي (تخصصات كـ مصفوفة دائماً لصحة العدد) */}
                    {(() => {
                      const rawSpec = photographer.specialties || photographer.specializations || [];
                      const specialtiesList = Array.isArray(rawSpec) ? rawSpec : (typeof rawSpec === 'string' ? rawSpec.split(',').map((s) => s.trim()).filter(Boolean) : []);
                      return (
                        <div className="flex flex-wrap gap-1.5 mb-3 items-center">
                          {specialtiesList.slice(0, 3).map((specialty, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-gradient-to-r from-primary/10 to-primary/5 text-primary text-xs font-semibold rounded-lg border border-primary/20"
                            >
                              {specialty}
                            </span>
                          ))}
                          {specialtiesList.length > 3 && (
                            <span className="text-xs font-semibold text-slate-500">(+{specialtiesList.length - 3})</span>
                          )}
                        </div>
                      );
                    })()}

                    {/* View Profile Button */}
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (photographerId) {
                          console.log('📍 Navigating to photographer profile, id:', photographerId);
                          navigate(`/photographers/${photographerId}`);
                        } else {
                          console.error('❌ Cannot navigate: photographerId is missing', photographer);
                        }
                      }}
                      className="w-full"
                      iconName="User"
                      iconPosition="right"
                    >
                      عرض الملف
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Scroll Indicators */}
          <div className="hidden md:block absolute top-1/2 -translate-y-1/2 left-0 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-white transition-colors z-10 opacity-0 group-hover:opacity-100">
            <Icon name="ChevronRight" size={24} className="text-primary" />
          </div>
          <div className="hidden md:block absolute top-1/2 -translate-y-1/2 right-0 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-white transition-colors z-10 opacity-0 group-hover:opacity-100">
            <Icon name="ChevronLeft" size={24} className="text-primary" />
          </div>
        </div>
        )}

        {/* View All Button - Mobile */}
        {photographers.length > 0 && (
          <div className="mt-6 text-center md:hidden">
            <Button
              variant="outline"
              onClick={() => navigate('/browse-photographers')}
              className="w-full"
              iconName="Search"
              iconPosition="right"
            >
              عرض الكل
            </Button>
          </div>
        )}

        {/* Subtle location nudge — opens map picker, works even after permission denial */}
        {!userLocation && !showLocationPrompt && photographers.length > 0 && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setShowMapModal(true)}
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-primary transition-colors"
            >
              <Icon name="MapPin" size={14} />
              <span>حدد موقعك لرؤية الأقرب إليك</span>
            </button>
          </div>
        )}

        {/* Map location picker modal */}
        {showMapModal && (
          <GlobalLocationModal
            isOpen={showMapModal}
            onClose={() => setShowMapModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default NearbyPhotographers;
